// High-entropy (non-animal) identity minting for the paid-privacy track.
//
// A free/animal identity is enumerable by design (a tiny word list, a derivable
// token — ADR 0004); a PAID identity is the opposite: a CSPRNG group makes the
// cohort non-discoverable and a CSPRNG label makes the token non-derivable. The
// backend already accepts opaque keys (ADR 0011), so this is a CLIENT-ONLY mint
// — no server, migration, or shared/token.ts change. See
// specs/2026-05-23-locus-high-entropy-keys.md.
//
// Runtime-neutral (only `crypto.getRandomValues`): imported by the SPA and
// hand-ported into the Android client. The privacy property IS the randomness,
// so the segments MUST come from a CSPRNG, never Math.random.

import { isValidGroupKey } from './token';

// Unambiguous alphabet — the same set `tokenFor` uses (drops i/l/o/1/0). 31
// symbols; a 26-char segment carries ~26·log2(31) ≈ 128 bits, comfortably
// inside the <=32-char isValidKeySegment limit.
export const HIGH_ENTROPY_ALPHA = 'abcdefghjkmnpqrstuvwxyz23456789';
export const SEGMENT_LEN = 26;
export const LABEL_SEGMENTS = 3; // keeps the 3-slot shape of the animal trio
export const GROUP_SEGMENTS = 2; // the cohort path g1-g2

// One CSPRNG segment over HIGH_ENTROPY_ALPHA. Rejection-free modulo bias is
// avoided by oversampling a byte per symbol and masking — but with a 31-symbol
// alphabet and 256-value bytes the bias is ~negligible; we still reject the
// out-of-range tail of the byte space to keep it uniform.
export function randomSegment(len = SEGMENT_LEN): string {
  const n = HIGH_ENTROPY_ALPHA.length;
  const max = Math.floor(256 / n) * n; // largest multiple of n <= 256
  let out = '';
  const buf = new Uint8Array(len * 2);
  while (out.length < len) {
    crypto.getRandomValues(buf);
    for (let i = 0; i < buf.length && out.length < len; i++) {
      if (buf[i] < max) out += HIGH_ENTROPY_ALPHA[buf[i] % n];
    }
  }
  return out;
}

export function randomLabel(): string[] {
  return Array.from({ length: LABEL_SEGMENTS }, () => randomSegment());
}

export function randomGroup(): string[] {
  return Array.from({ length: GROUP_SEGMENTS }, () => randomSegment());
}

// Parse a join key — the 2-segment group secret carried out of band as
// `<g1>-<g2>` (also the tail of a `#/join/<g1>-<g2>` link). Returns the group
// pair if valid, else null. Segments may be animal keys or high-entropy; both
// satisfy isValidGroupKey.
export function parseJoinKey(raw: string): string[] | null {
  const s = (raw || '').trim();
  // Tolerate a full hash link or a bare code.
  const m = /(?:#\/join\/)?([a-z0-9_]{1,32}-[a-z0-9_]{1,32})\/?$/i.exec(s);
  const body = m ? m[1] : s;
  const parts = body.split('-');
  if (parts.length !== 2) return null;
  return isValidGroupKey(parts) ? parts : null;
}

// The group key written as the shareable join code.
export function joinCode(group: string[] | null | undefined): string | null {
  if (!group || group.length !== 2) return null;
  return `${group[0]}-${group[1]}`;
}

// A device is "high-entropy" (non-animal) when a label segment is not one of the
// known animal keys. Callers use this to pick the colour-swatch avatar fallback
// instead of animal art. `animalKeys` is the SPA's ANIMALS vocabulary.
export function isHighEntropyLabel(label: string[] | null | undefined, animalKeys: Set<string>): boolean {
  if (!Array.isArray(label) || label.length === 0) return false;
  return label.some((k) => !animalKeys.has(k));
}
